<script setup lang="ts">
import { computed, unref } from 'vue';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';
import { useCustomFormsStorageContext } from '@satellite/composables/useCustomFormsStorageContext';

import DocumentUploadButton from '../DocumentUploadButton.vue';
import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Document field type - single file upload.
 * Requires storage context via CUSTOM_FORMS_STORAGE_CONTEXT_KEY (warehouseId, storageEndpoint).
 */
const props = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  input: [value: string];
}>();

const { normalizedProps } = useCustomFieldProps(props);
const storageContext = useCustomFormsStorageContext();
const { rules: validationRules } = useCustomFieldValidation(computed(() => props.field));

const modelValueString = computed(() =>
  typeof props.modelValue === 'string' ? props.modelValue : ''
);

function handleUpdate(value: string | string[]) {
  const v = Array.isArray(value) ? value[0] ?? '' : value ?? '';
  emit('update:model-value', v);
  emit('input', v);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <div class="custom-field-document">
      <document-upload-button
        :warehouse-id="unref(storageContext).warehouseId"
        :storage-endpoint="unref(storageContext).storageEndpoint"
        :model-value="unref(modelValueString)"
        :required="normalizedProps.required"
        :rules="validationRules"
        :read-only="normalizedProps.readOnly"
        :label="field.label ?? 'Upload document'"
        :placeholder="field.placeholder"
        :multiple="false"
        :variant="outlined ? 'simple' : 'default'"
        @update:model-value="handleUpdate" />
    </div>
  </custom-field-input-base>
</template>

<style scoped lang="scss">
.custom-field-document {
  width: 100%;
}
</style>
